import React, { useState } from 'react';
import {
  Store,
  Plus,
  RefreshCw,
  Loader2,
  Rss,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { Shop, ShopPlatform } from '../../services/feedWatch';

// ── Platform config ─────────────────────────────────────────────

const PLATFORM_CONFIG: Record<ShopPlatform, { label: string; className: string }> = {
  shopify: { label: 'Shopify', className: 'bg-green-500/10 text-green-500 border-green-500/20' },
  woocommerce: { label: 'WooCommerce', className: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
  magento: { label: 'Magento', className: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
  bigcommerce: { label: 'BigCommerce', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  custom: { label: 'Custom', className: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
  other: { label: 'Other', className: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
};

export function PlatformBadge({ platform }: { platform: ShopPlatform }) {
  const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.other;
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

// ── Create Shop Dialog ──────────────────────────────────────────

interface CreateShopDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (data: { name: string; platform?: ShopPlatform; shop_url?: string }) => void;
  isCreating: boolean;
}

function CreateShopDialog({ open, onOpenChange, onCreate, isCreating }: CreateShopDialogProps) {
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<ShopPlatform>('shopify');
  const [shopUrl, setShopUrl] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      name,
      platform,
      ...(shopUrl.trim() ? { shop_url: shopUrl.trim() } : {}),
    });
    setName('');
    setPlatform('shopify');
    setShopUrl('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Shop</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="shop-name">Shop Name</Label>
            <Input
              id="shop-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Store"
              className="mt-1.5 h-11 rounded-xl border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div>
            <Label htmlFor="shop-platform">Platform</Label>
            <Select value={platform} onValueChange={(v) => setPlatform(v as ShopPlatform)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shopify">Shopify</SelectItem>
                <SelectItem value="woocommerce">WooCommerce</SelectItem>
                <SelectItem value="magento">Magento</SelectItem>
                <SelectItem value="bigcommerce">BigCommerce</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="shop-url">Shop URL (optional)</Label>
            <Input
              id="shop-url"
              value={shopUrl}
              onChange={(e) => setShopUrl(e.target.value)}
              placeholder="https://mystore.com"
              className="mt-1.5 h-11 rounded-xl border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="rounded-lg" disabled={!name.trim() || isCreating}>
              {isCreating && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Create Shop
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Shop List ───────────────────────────────────────────────────

interface ShopListProps {
  shops: Shop[];
  isLoading: boolean;
  onSelectShop: (shop: Shop) => void;
  onCreateShop: (data: { name: string; platform?: ShopPlatform; shop_url?: string }) => void;
  onRefresh: () => void;
  isCreating: boolean;
}

export function ShopList({ shops, isLoading, onSelectShop, onCreateShop, onRefresh, isCreating }: ShopListProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCreate = (data: { name: string; platform?: ShopPlatform; shop_url?: string }) => {
    onCreateShop(data);
    setDialogOpen(false);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Shops</h1>
          <p className="text-muted-foreground mt-1">
            Manage your shops and their product collections
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="rounded-lg" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button className="rounded-lg" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Shop
          </Button>
        </div>
      </div>

      {/* Shop cards */}
      {isLoading && shops.length === 0 ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      ) : shops.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed rounded-xl">
          <Store className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-1">No shops yet</h3>
          <p className="text-muted-foreground mb-6">
            Create your first shop to start managing product collections.
          </p>
          <Button className="rounded-lg" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create First Shop
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {shops.map((shop) => (
            <Card
              key={shop.id}
              className="group border-2 rounded-xl p-5 cursor-pointer hover:border-primary/40 hover:shadow-lg transition-all duration-200"
              onClick={() => onSelectShop(shop)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Store className="h-5 w-5 text-primary" />
                </div>
                <PlatformBadge platform={shop.platform} />
              </div>

              <h3 className="text-lg font-semibold mb-1 group-hover:text-primary transition-colors">
                {shop.name}
              </h3>

              {shop.shop_url && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                  <ExternalLink className="h-3 w-3 shrink-0" />
                  <span className="truncate">{shop.shop_url}</span>
                </div>
              )}

              <div className="flex items-center justify-between mt-4 pt-3 border-t">
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Rss className="h-3.5 w-3.5" />
                  <span>
                    <span className="font-medium text-foreground">{shop.feed_count}</span>{' '}
                    {shop.feed_count === 1 ? 'collection' : 'collections'}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(shop.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CreateShopDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreate={handleCreate}
        isCreating={isCreating}
      />
    </div>
  );
}
